// ============================================================================
// KLINISOL CRO - Training Matrix Dashboard
// File: src/pages/TrainingMatrixDashboard.js
// Purpose: RACI-based training compliance visualization
// FDA 21 CFR Part 11 / ICH-GCP E6(R3) Compliant
// Version: 2.2 - Audit-Proof Help Modal, All Training Modules
// Last Updated: December 3, 2025
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import trainingMatrixApi from '../services/trainingMatrixApi';
import {
    Users,
    AlertTriangle,
    CheckCircle,
    Clock,
    XCircle,
    ChevronDown,
    ChevronRight,
    Search,
    Filter,
    User,
    ArrowLeft,
    RefreshCw,
    Info,
    UserPlus,
    X,
    HelpCircle,
    BookOpen,
    Shield,
    FileText,
    Award
} from 'lucide-react';

// Brand colors
const COLORS = {
    slateGrey: '#475569',
    slateDark: '#334155',
    slateMedium: '#64748B',
    slateLight: '#94A3B8',
    magenta: '#E91E63',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6'
};

// ============================================================================
// STATUS BADGE COMPONENT
// ============================================================================
const StatusBadge = ({ status }) => {
    const configs = {
        current: { bg: 'bg-green-100', text: 'text-green-800', label: 'Current' },
        expiring_soon: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Expiring Soon' },
        expired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired' },
        not_started: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Not Started' },
        in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' }
    };
    
    const config = configs[status] || configs.not_started;
    
    return (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
            {config.label}
        </span>
    );
};

// ============================================================================
// RACI BADGE COMPONENT - KLINISOL Brand Colors (White BG + Colored Text)
// ============================================================================
const RACIBadge = ({ raci, level }) => {
    // KLINISOL Brand-aligned RACI colors - White background with colored text
    const configs = {
        'A/R': { text: '#E91E63' },  // Magenta - Accountable & Responsible
        'A': { text: '#C2185B' },    // Magenta Dark - Accountable
        'R': { text: '#D81B60' },    // Rose - Responsible
        'C': { text: '#475569' },    // Slate Dark - Consulted
        'I': { text: '#64748B' }     // Slate Medium - Informed
    };
    
    const config = configs[raci] || configs['I'];
    
    return (
        <div className="flex flex-col items-center">
            <span 
                className="w-8 h-8 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white hover:shadow-md hover:border-slate-400 transition-all cursor-default"
                style={{ color: config.text }}
            >
                {raci}
            </span>
            {level && (
                <span className="text-xs text-gray-500 mt-1">
                    {level === 'full' ? 'Full' : level === 'awareness' ? 'Aware' : '-'}
                </span>
            )}
        </div>
    );
};

// ============================================================================
// COMPLIANCE RING COMPONENT
// ============================================================================
const ComplianceRing = ({ percentage, size = 60 }) => {
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    
    let color = COLORS.danger;
    if (percentage >= 90) color = COLORS.success;
    else if (percentage >= 70) color = COLORS.warning;
    
    return (
        <div className="relative inline-flex items-center justify-center">
            <svg width={size} height={size}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="6"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth="6"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <span className="absolute text-sm font-bold" style={{ color }}>
                {percentage}%
            </span>
        </div>
    );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
const TrainingMatrixDashboard = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    
    // Data states
    const [functionalRoles, setFunctionalRoles] = useState([]);
    const [raciMatrix, setRaciMatrix] = useState({ matrix: [], categories: [] });
    const [myCompliance, setMyCompliance] = useState(null);
    const [allUsersCompliance, setAllUsersCompliance] = useState([]);
    const [expiringTraining, setExpiringTraining] = useState({ items: [], count: 0 });
    
    // UI states
    const [expandedCategories, setExpandedCategories] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserModal, setShowUserModal] = useState(false);
    
    // Role Assignment Modal states
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [roleAssignUserId, setRoleAssignUserId] = useState('');
    const [roleAssignFunctionalRoleId, setRoleAssignFunctionalRoleId] = useState('');
    const [roleAssignReason, setRoleAssignReason] = useState('');
    const [assigningRole, setAssigningRole] = useState(false);
    const [roleAssignError, setRoleAssignError] = useState('');

    // Permission checks
    const isAdmin = ['Super Admin', 'Admin', 'QA'].includes(user?.role);
    const canManageRoles = ['Super Admin', 'QA'].includes(user?.role);

    // ========================================================================
    // DATA LOADING
    // ========================================================================
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Load functional roles
            const roles = await trainingMatrixApi.getFunctionalRoles();
            setFunctionalRoles(roles);
            
            // Load RACI matrix
            const matrix = await trainingMatrixApi.getRACIMatrix();
            setRaciMatrix(matrix);
            
            // Initialize expanded categories
            const expanded = {};
            matrix.categories.forEach(cat => {
                expanded[cat.category] = true;
            });
            setExpandedCategories(expanded);
            
            // Load my compliance
            const compliance = await trainingMatrixApi.getMyCompliance();
            setMyCompliance(compliance);
            
            // Admin-only data
            if (isAdmin) {
                const usersCompliance = await trainingMatrixApi.getAllUsersComplianceSummary();
                setAllUsersCompliance(usersCompliance);
                
                const expiring = await trainingMatrixApi.getExpiringTraining(30);
                setExpiringTraining(expiring);
            }
            
        } catch (err) {
            console.error('Error loading data:', err);
            setError('Failed to load training matrix data');
        } finally {
            setLoading(false);
        }
    };

    // ========================================================================
    // HANDLERS
    // ========================================================================
    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleViewUser = async (userId) => {
        try {
            const userData = await trainingMatrixApi.getUserCompliance(userId);
            setSelectedUser(userData);
            setShowUserModal(true);
        } catch (err) {
            console.error('Error loading user:', err);
        }
    };

    // Role Assignment Handlers
    const openRoleModal = () => {
        setRoleAssignUserId('');
        setRoleAssignFunctionalRoleId('');
        setRoleAssignReason('');
        setRoleAssignError('');
        setShowRoleModal(true);
    };

    const handleAssignRole = async () => {
        if (!roleAssignUserId || !roleAssignFunctionalRoleId) {
            setRoleAssignError('Please select a user and a functional role');
            return;
        }
        if (!roleAssignReason.trim()) {
            setRoleAssignError('Assignment reason is required for audit trail');
            return;
        }

        setAssigningRole(true);
        setRoleAssignError('');

        try {
            await trainingMatrixApi.assignFunctionalRole({
                userId: parseInt(roleAssignUserId),
                functionalRoleId: parseInt(roleAssignFunctionalRoleId),
                effectiveDate: new Date().toISOString().split('T')[0],
                reason: roleAssignReason.trim()
            });

            setShowRoleModal(false);
            // Reload data to reflect changes
            await loadData();
        } catch (err) {
            console.error('Error assigning role:', err);
            setRoleAssignError(err.response?.data?.message || 'Failed to assign role');
        } finally {
            setAssigningRole(false);
        }
    };

    // ========================================================================
    // FILTERED DATA
    // ========================================================================
    const filteredUsers = allUsersCompliance.filter(u => {
        const matchesSearch = searchTerm === '' || 
            u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const groupedMatrix = raciMatrix.matrix.reduce((acc, item) => {
        if (!acc[item.category]) {
            acc[item.category] = [];
        }
        if (selectedCategory === 'all' || item.category === selectedCategory) {
            acc[item.category].push(item);
        }
        return acc;
    }, {});

    // ========================================================================
    // HELP MODAL COMPONENT
    // ========================================================================
    const HelpModal = () => {
        if (!showHelp) return null;

        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="sticky top-0 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
                        <div className="flex items-center gap-3">
                            <BookOpen className="w-6 h-6" />
                            <h3 className="font-bold text-xl">Training Matrix Guide</h3>
                        </div>
                        <button 
                            onClick={() => setShowHelp(false)}
                            className="hover:bg-white/20 rounded-lg p-2 transition-all"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        
                        {/* Governing Document - AUDIT CRITICAL */}
                        <section className="bg-slate-100 border-l-4 border-[#475569] rounded-r-lg p-4">
                            <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                <span>Governing Document</span>
                            </h4>
                            <p className="text-sm text-slate-700">
                                This Training Matrix is maintained per <strong>QMS-002 v1.1: Training and Competency Management</strong>. 
                                All RACI assignments are reviewed annually and approved by the Clinical & Regulatory Lead. 
                                Training requirements are aligned with role-specific competency expectations as defined in 
                                ICH E6(R3) Section 2.8.
                            </p>
                        </section>

                        {/* Why RACI for Training - AUDIT JUSTIFICATION */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">Why RACI for Training?</h4>
                            <p className="text-gray-700 mb-3">
                                RACI ensures training assignments are <strong className="text-[#E91E63]">intentional, documented, and defensible</strong> during 
                                FDA inspections and sponsor audits. Each assignment reflects your role's actual responsibilities 
                                per ICH E6(R3) guidelines and supports the quality-by-design principle.
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#E91E63' }}>A</span>
                                        <span className="font-bold text-pink-900">Accountable</span>
                                    </div>
                                    <div className="text-xs text-pink-800">Ultimate ownership - signs off on completion and compliance</div>
                                </div>
                                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#D81B60' }}>R</span>
                                        <span className="font-bold text-slate-900">Responsible</span>
                                    </div>
                                    <div className="text-xs text-slate-800">Performs the work - must complete training and demonstrate competency</div>
                                </div>
                                <div className="bg-gray-50 border border-gray-300 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#475569' }}>C</span>
                                        <span className="font-bold text-gray-900">Consulted</span>
                                    </div>
                                    <div className="text-xs text-gray-700">Subject matter expert - provides input, awareness training required</div>
                                </div>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-6 h-6 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#64748B' }}>I</span>
                                        <span className="font-bold text-gray-900">Informed</span>
                                    </div>
                                    <div className="text-xs text-gray-700">Awareness only - no formal training required (see note below)</div>
                                </div>
                            </div>
                        </section>

                        {/* Training Levels */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">Training Levels</h4>
                            <div className="space-y-2">
                                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <Award className="w-5 h-5 text-green-600" />
                                    <div>
                                        <span className="font-bold text-green-900">Full Training</span>
                                        <span className="text-sm text-green-800 ml-2">Complete module + pass assessment (≥80%) + electronic signature</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <FileText className="w-5 h-5 text-blue-600" />
                                    <div>
                                        <span className="font-bold text-blue-900">Awareness</span>
                                        <span className="text-sm text-blue-800 ml-2">Review content + acknowledgment signature (no assessment required)</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Compliance Status Definitions - NEW */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">Compliance Status Definitions</h4>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <div>
                                        <span className="font-bold text-green-900 text-sm">Current</span>
                                        <p className="text-xs text-green-700">Training completed and within validity period</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <Clock className="w-4 h-4 text-yellow-600" />
                                    <div>
                                        <span className="font-bold text-yellow-900 text-sm">Expiring Soon</span>
                                        <p className="text-xs text-yellow-700">Due for recertification within 30 days</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertTriangle className="w-4 h-4 text-red-600" />
                                    <div>
                                        <span className="font-bold text-red-900 text-sm">Expired</span>
                                        <p className="text-xs text-red-700">Past recertification date - immediate action required</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                    <XCircle className="w-4 h-4 text-gray-500" />
                                    <div>
                                        <span className="font-bold text-gray-900 text-sm">Not Started</span>
                                        <p className="text-xs text-gray-600">Training not yet completed</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Recertification Policy - NEW */}
                        <section className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                                <RefreshCw className="w-4 h-4" />
                                <span>Recertification Policy</span>
                            </h4>
                            <p className="text-sm text-amber-800 mb-2">
                                All training requires <strong>annual recertification (12 months)</strong> from the date of completion. 
                                This ensures personnel maintain current knowledge of evolving regulations and procedures.
                            </p>
                            <ul className="text-sm text-amber-800 space-y-1">
                                <li>• Notification sent 30 days before expiration</li>
                                <li>• Expired training must be completed before performing related duties</li>
                                <li>• Recertification may require updated assessment</li>
                            </ul>
                        </section>

                        {/* About Informed Status - NEW */}
                        <section className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                            <h4 className="font-bold text-slate-900 mb-2">About "Informed" (I) Status</h4>
                            <p className="text-sm text-slate-700">
                                Roles marked <strong>"Informed"</strong> are not required to complete formal training for that module. 
                                However, these personnel should be <strong>aware the procedure exists</strong> and may be notified of 
                                significant changes. No training record or completion signature is required for Informed assignments.
                            </p>
                        </section>

                        {/* How to Read the Matrix */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">How to Read the Matrix</h4>
                            <ol className="space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">1.</span>
                                    <span>Find your <strong>functional role</strong> in the column headers (CRL, DBL, PM, etc.)</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">2.</span>
                                    <span>Scan down your column to see your <strong>RACI assignment</strong> for each module</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">3.</span>
                                    <span>Check the <strong>training level</strong> below each badge (Full or Awareness)</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-[#E91E63] flex-shrink-0">4.</span>
                                    <span>Complete required training before <strong>expiration date</strong> to maintain compliance</span>
                                </li>
                            </ol>
                        </section>

                        {/* Functional Roles */}
                        <section>
                            <h4 className="font-bold text-gray-900 text-lg mb-3">Functional Roles</h4>
                            <p className="text-xs text-gray-500 mb-2">Internal KLINISOL staff and contracted site personnel</p>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="p-2 bg-blue-50 border border-blue-200 rounded"><strong>CRL</strong> - Clinical & Regulatory Lead</div>
                                <div className="p-2 bg-blue-50 border border-blue-200 rounded"><strong>DBL</strong> - Data & Biostatistics Lead</div>
                                <div className="p-2 bg-blue-50 border border-blue-200 rounded"><strong>PM</strong> - Project Manager</div>
                                <div className="p-2 bg-purple-50 border border-purple-200 rounded"><strong>QAC</strong> - External QA Consultant</div>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded"><strong>CRA</strong> - Clinical Research Associate</div>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded"><strong>PI</strong> - Principal Investigator</div>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded"><strong>SUB</strong> - Sub-Investigator</div>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded"><strong>SC</strong> - Study Coordinator</div>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded"><strong>RN</strong> - Research Nurse</div>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded"><strong>RC</strong> - Regulatory Coordinator</div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                <span className="inline-block w-3 h-3 bg-blue-50 border border-blue-200 rounded mr-1"></span> KLINISOL Core Team
                                <span className="inline-block w-3 h-3 bg-purple-50 border border-purple-200 rounded ml-3 mr-1"></span> External Consultant
                                <span className="inline-block w-3 h-3 bg-gray-50 border border-gray-200 rounded ml-3 mr-1"></span> Contract/Site Personnel
                            </p>
                        </section>

                        {/* Regulatory Compliance */}
                        <section className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                <span>Regulatory Compliance</span>
                            </h4>
                            <p className="text-sm text-green-800 mb-2">
                                This Training Matrix is aligned with <strong>ICH E6(R3) GCP Guidelines</strong> (effective July 2025) 
                                and designed for <strong>FDA 21 CFR Part 11</strong> compliance.
                            </p>
                            <ul className="text-sm text-green-800 space-y-1">
                                <li>• Electronic signatures with complete audit trail</li>
                                <li>• Documented training records with timestamps</li>
                                <li>• Role-based access controls</li>
                                <li>• Automatic expiration tracking and notifications</li>
                                <li>• Blockchain-verified certificate integrity</li>
                            </ul>
                        </section>

                        {/* Questions and Support - AUDIT CRITICAL */}
                        <section className="bg-[#E91E63]/10 border border-[#E91E63]/30 rounded-lg p-4">
                            <h4 className="font-bold text-[#E91E63] mb-2 flex items-center gap-2">
                                <HelpCircle className="w-4 h-4" />
                                <span>Questions or Concerns?</span>
                            </h4>
                            <div className="text-sm text-gray-700 space-y-2">
                                <p><strong>Training assignments:</strong> Contact the <strong>Clinical & Regulatory Lead (CRL)</strong></p>
                                <p><strong>Completion status:</strong> Contact the <strong>Project Manager (PM)</strong></p>
                                <p><strong>Compliance concerns:</strong> Escalate to the <strong>Quality Assurance Coordinator (QAC)</strong></p>
                            </div>
                        </section>

                        {/* Version Control */}
                        <section className="text-center text-sm text-gray-500 pt-4 border-t">
                            <p>Governed by: <strong>QMS-002 v1.1</strong> Training and Competency Management</p>
                            <p className="mt-1">KLINISOL CRO Quality Management System</p>
                        </section>

                    </div>
                </div>
            </div>
        );
    };

    // ========================================================================
    // RENDER: LOADING STATE
    // ========================================================================
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: COLORS.magenta }} />
                    <p className="text-gray-600">Loading Training Matrix...</p>
                </div>
            </div>
        );
    }

    // ========================================================================
    // RENDER: ERROR STATE
    // ========================================================================
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                    <p className="text-red-600 mb-4">{error}</p>
                    <button 
                        onClick={loadData}
                        className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // ========================================================================
    // RENDER: MAIN DASHBOARD
    // ========================================================================
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Help Modal */}
            <HelpModal />

            {/* Gradient Banner Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => window.history.back()}
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-6 h-6" />
                            </button>
                            
                            <BookOpen className="w-8 h-8" />
                            <div>
                                <h1 className="text-2xl font-bold">Training Matrix</h1>
                                <p className="text-sm opacity-90">RACI-Based Training Requirements • Aligned with ICH E6(R3) Guidelines • QMS-002 v1.1</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowHelp(true)}
                                className="px-4 py-2 border border-white/30 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
                            >
                                <HelpCircle className="w-4 h-4" />
                                Help
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Tabs - Below Banner */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4">
                    <nav className="flex space-x-8">
                        {['overview', 'staff', 'matrix'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`py-4 px-1 font-medium text-sm border-b-2 transition-colors ${
                                    activeTab === tab
                                        ? 'border-[#E91E63] text-[#E91E63]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab === 'overview' && 'Overview'}
                                {tab === 'staff' && 'Staff Compliance'}
                                {tab === 'matrix' && 'RACI Matrix'}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        {/* My Training Status */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center mb-4">
                                <User className="w-5 h-5 mr-2 text-gray-400" />
                                <h2 className="text-lg font-semibold">My Training Status</h2>
                            </div>
                            
                            {myCompliance?.hasRole ? (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500">Functional Role</p>
                                        <p className="text-xl font-bold">{myCompliance.functionalRole?.name}</p>
                                        <p className="text-sm text-gray-400">({myCompliance.functionalRole?.code})</p>
                                    </div>
                                    <div className="flex items-center space-x-8">
                                        <ComplianceRing 
                                            percentage={myCompliance.summary?.compliancePercentage || 0} 
                                            size={80} 
                                        />
                                        <div className="grid grid-cols-2 gap-4 text-center">
                                            <div>
                                                <p className="text-2xl font-bold">{myCompliance.summary?.total || 0}</p>
                                                <p className="text-xs text-gray-500">Required</p>
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-green-600">{myCompliance.summary?.completed || 0}</p>
                                                <p className="text-xs text-gray-500">Completed</p>
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-yellow-600">{myCompliance.summary?.expiringSoon || 0}</p>
                                                <p className="text-xs text-gray-500">Expiring</p>
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-red-600">
                                                    {(myCompliance.summary?.expired || 0) + (myCompliance.summary?.notStarted || 0)}
                                                </p>
                                                <p className="text-xs text-gray-500">Overdue</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <Info className="w-8 h-8 mx-auto mb-2" />
                                    <p>No functional role assigned</p>
                                    <p className="text-sm">Contact your administrator to get assigned to a role</p>
                                </div>
                            )}
                        </div>

                        {/* Admin Stats */}
                        {isAdmin && (
                            <>
                                {/* Stats Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="bg-white rounded-lg shadow p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500">Total Staff</p>
                                                <p className="text-2xl font-bold">{allUsersCompliance.length}</p>
                                            </div>
                                            <Users className="w-8 h-8 text-gray-400" />
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-lg shadow p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500">Fully Compliant</p>
                                                <p className="text-2xl font-bold text-green-600">
                                                    {allUsersCompliance.filter(u => u.summary?.compliancePercentage === 100).length}
                                                </p>
                                            </div>
                                            <CheckCircle className="w-8 h-8 text-green-400" />
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-lg shadow p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500">Expiring Soon</p>
                                                <p className="text-2xl font-bold text-yellow-600">{expiringTraining.count}</p>
                                            </div>
                                            <Clock className="w-8 h-8 text-yellow-400" />
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-lg shadow p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500">Needs Attention</p>
                                                <p className="text-2xl font-bold text-red-600">
                                                    {allUsersCompliance.filter(u => u.summary && u.summary.compliancePercentage < 100).length}
                                                </p>
                                            </div>
                                            <AlertTriangle className="w-8 h-8 text-red-400" />
                                        </div>
                                    </div>
                                </div>

                                {/* Expiring Training Alert */}
                                {expiringTraining.count > 0 && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                        <h3 className="font-semibold text-yellow-800 flex items-center mb-3">
                                            <AlertTriangle className="w-5 h-5 mr-2" />
                                            Training Expiring Within 30 Days
                                        </h3>
                                        <div className="space-y-2">
                                            {expiringTraining.items.slice(0, 5).map((item, idx) => (
                                                <div key={idx} className="flex justify-between items-center text-sm">
                                                    <span>{item.first_name} {item.last_name} - {item.sop_title}</span>
                                                    <span className="text-yellow-700">
                                                        {item.days_until_expiry} days
                                                    </span>
                                                </div>
                                            ))}
                                            {expiringTraining.count > 5 && (
                                                <p className="text-sm text-yellow-700">
                                                    + {expiringTraining.count - 5} more...
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Staff Compliance Tab */}
                {activeTab === 'staff' && isAdmin && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-4 border-b">
                            <div className="flex items-center justify-between">
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name or email..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                                    />
                                </div>
                                {canManageRoles && (
                                    <button
                                        onClick={openRoleModal}
                                        className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90 ml-4"
                                        style={{ backgroundColor: COLORS.slateGrey }}
                                    >
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Assign Role
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff Member</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">System Role</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Functional Role</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Compliance</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Current</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Expiring</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Overdue</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredUsers.map(u => (
                                        <tr key={u.userId} className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <div>
                                                    <p className="font-medium">{u.firstName} {u.lastName}</p>
                                                    <p className="text-sm text-gray-500">{u.email}</p>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{u.systemRole}</td>
                                            <td className="px-4 py-3">
                                                {u.functionalRole ? (
                                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                                                        {u.functionalRole.code}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">Not Assigned</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {u.summary ? (
                                                    <ComplianceRing percentage={u.summary.compliancePercentage} size={40} />
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center text-green-600 font-medium">
                                                {u.summary?.completed || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center text-yellow-600 font-medium">
                                                {u.summary?.expiringSoon || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center text-red-600 font-medium">
                                                {u.summary ? (u.summary.expired + u.summary.notStarted) : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => handleViewUser(u.userId)}
                                                    className="text-pink-600 hover:text-pink-800 text-sm font-medium"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* RACI Matrix Tab */}
                {activeTab === 'matrix' && (
                    <div className="space-y-4">
                        {/* Category Filter */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="flex items-center space-x-4">
                                <Filter className="w-4 h-4 text-gray-400" />
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                                >
                                    <option value="all">All Categories</option>
                                    {raciMatrix.categories.map(cat => (
                                        <option key={cat.category} value={cat.category}>
                                            {cat.category} ({cat.count})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Matrix by Category */}
                        {Object.entries(groupedMatrix).map(([category, sops]) => (
                            sops.length > 0 && (
                                <div key={category} className="bg-white rounded-lg shadow overflow-hidden">
                                    <button
                                        onClick={() => toggleCategory(category)}
                                        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
                                    >
                                        <div className="flex items-center">
                                            {expandedCategories[category] ? (
                                                <ChevronDown className="w-5 h-5 mr-2 text-gray-500" />
                                            ) : (
                                                <ChevronRight className="w-5 h-5 mr-2 text-gray-500" />
                                            )}
                                            <span className="font-semibold">{category}</span>
                                            <span className="ml-2 text-sm text-gray-500">({sops.length} Modules)</span>
                                        </div>
                                    </button>
                                    
                                    {expandedCategories[category] && (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-1/3">Module</th>
                                                        {functionalRoles.map(role => (
                                                            <th key={role.code} className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                                                {role.code}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y">
                                                    {sops.map(sop => (
                                                        <tr key={sop.sopId || sop.sourceId} className="hover:bg-gray-50">
                                                            <td className="px-4 py-3">
                                                                <p className="font-medium text-sm">{sop.moduleCode || sop.sopNumber}</p>
                                                                <p className="text-xs text-gray-500">{sop.moduleTitle || sop.sopTitle}</p>
                                                            </td>
                                                            {functionalRoles.map(role => {
                                                                const roleData = sop.roles ? sop.roles[role.code] : null;
                                                                return (
                                                                    <td key={role.code} className="px-4 py-3 text-center">
                                                                        {roleData ? (
                                                                            <RACIBadge raci={roleData.raci} level={roleData.level} />
                                                                        ) : (
                                                                            <span className="text-gray-300">-</span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )
                        ))}

                        {/* RACI Legend - Updated with Brand Colors */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <h3 className="font-semibold mb-3">RACI Legend</h3>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                <div className="flex items-center space-x-2">
                                    <span className="w-8 h-8 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#E91E63' }}>A/R</span>
                                    <span>Accountable & Responsible</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="w-8 h-8 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#C2185B' }}>A</span>
                                    <span>Accountable</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="w-8 h-8 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#D81B60' }}>R</span>
                                    <span>Responsible</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="w-8 h-8 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#475569' }}>C</span>
                                    <span>Consulted</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className="w-8 h-8 flex items-center justify-center text-xs font-bold rounded border-2 border-slate-300 bg-white" style={{ color: '#64748B' }}>I</span>
                                    <span>Informed</span>
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t">
                                <h4 className="font-medium mb-2">Training Levels</h4>
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                    <div><strong>Full:</strong> Complete module + quiz + signature</div>
                                    <div><strong>Awareness:</strong> Review module content</div>
                                    <div><strong>None:</strong> No training required</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* User Detail Modal */}
            {showUserModal && selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold">
                                    {selectedUser.user.firstName} {selectedUser.user.lastName}
                                </h2>
                                <p className="text-sm text-gray-500">{selectedUser.user.email}</p>
                            </div>
                            <button
                                onClick={() => setShowUserModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XCircle className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="p-6">
                            {!selectedUser.hasRole ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Info className="w-8 h-8 mx-auto mb-2" />
                                    <p>No functional role assigned</p>
                                </div>
                            ) : (
                                <>
                                    {/* Summary */}
                                    <div className="mb-6 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500">Functional Role</p>
                                            <p className="font-semibold">{selectedUser.functionalRole.name}</p>
                                        </div>
                                        <ComplianceRing percentage={selectedUser.summary?.compliancePercentage || 0} size={60} />
                                    </div>
                                    
                                    {/* Requirements Table */}
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Module</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">RACI</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Completed</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Expires</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {selectedUser.requirements.map((req, idx) => (
                                                <tr key={idx}>
                                                    <td className="px-4 py-2">
                                                        <p className="font-medium text-sm">{req.moduleCode || req.sopNumber}</p>
                                                        <p className="text-xs text-gray-500">{req.moduleTitle || req.sopTitle}</p>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <RACIBadge raci={req.raci} level={req.trainingLevel} />
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <StatusBadge status={req.status} />
                                                    </td>
                                                    <td className="px-4 py-2 text-sm">
                                                        {req.completedAt ? new Date(req.completedAt).toLocaleDateString() : '-'}
                                                    </td>
                                                    <td className="px-4 py-2 text-sm">
                                                        {req.expirationDate ? new Date(req.expirationDate).toLocaleDateString() : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Role Assignment Modal */}
            {showRoleModal && canManageRoles && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6 border-b flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold" style={{ color: COLORS.slateGrey }}>
                                    Assign Functional Role
                                </h2>
                                <p className="text-sm text-gray-500">FDA 21 CFR Part 11 Compliant</p>
                            </div>
                            <button
                                onClick={() => setShowRoleModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            {roleAssignError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                    {roleAssignError}
                                </div>
                            )}

                            {/* User Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Select User *
                                </label>
                                <select
                                    value={roleAssignUserId}
                                    onChange={(e) => setRoleAssignUserId(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                                >
                                    <option value="">-- Select User --</option>
                                    {allUsersCompliance.map(u => (
                                        <option key={u.userId} value={u.userId}>
                                            {u.firstName} {u.lastName} ({u.email})
                                            {u.functionalRole ? ` - Current: ${u.functionalRole.code}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Functional Role Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Functional Role *
                                </label>
                                <select
                                    value={roleAssignFunctionalRoleId}
                                    onChange={(e) => setRoleAssignFunctionalRoleId(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                                >
                                    <option value="">-- Select Role --</option>
                                    {functionalRoles.map(role => (
                                        <option key={role.id} value={role.id}>
                                            {role.code} - {role.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Assignment Reason */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Assignment Reason * (Audit Trail)
                                </label>
                                <textarea
                                    value={roleAssignReason}
                                    onChange={(e) => setRoleAssignReason(e.target.value)}
                                    placeholder="e.g., Initial role assignment, Role change due to promotion"
                                    rows={3}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                                />
                            </div>

                            {/* Info Box */}
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-start">
                                <Info className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                                <span>
                                    Assigning a new role will end any previous role assignment for this user. 
                                    All changes are recorded in the audit trail.
                                </span>
                            </div>
                        </div>

                        <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowRoleModal(false)}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAssignRole}
                                disabled={assigningRole}
                                className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                                style={{ backgroundColor: COLORS.magenta }}
                            >
                                {assigningRole ? 'Assigning...' : 'Assign Role'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingMatrixDashboard;
